import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import { BsPersonCircle } from "react-icons/bs";
import { TbLogout } from "react-icons/tb";
import RightTimeApi from "@/API";
import Helpers from "@/Helpers";
import UserEditModal from "@modals/UserEditModal";
import HelpEditModal from "@modals/HelpEditModal";
import ReleaseHistoryModal from "@modals/ReleaseHistoryModal";
import { decodeHelpLanguage, normalizeHelpFormat, readHelpEntryFormat, type HelpLanguageRecord } from "@/utils/helpContentCodec";
import { buildHelpDetailPagePath, helpEntryHasDetailContent } from "@/utils/helpDetailContent";
import { renderFormattedContent } from "@/utils/renderFormattedContent";
import Enums from "@/enums";
import { auditVersionChangesAsync } from "@/utils/versionAuditLog";
import { clearSiteData } from "@/utils/clearSiteData";
import { readPermIdsFromSession, safeReadCurrentUserFromSession } from "@/i18n/sessionUser";
import { filterSupportedLanguages } from "@/utils/languageUtils";

type CurrentUser = {
  userId?: number;
  firstName?: string;
  lastName?: string;
  languageId?: number | string;
  defaultLanguageId?: number | string;
  showHelp?: boolean;
  permIds?: number[];
};

type HelpEntry = {
  id?: number;
  section?: string;
  identifier?: string;
  companyId?: number | null;
  format?: number | string | null;
  languages?: HelpLanguageRecord[];
};

type SupportedLanguage = {
  id?: number | string;
  name?: string;
  abbrev?: string;
};

const SITE_HELP_SESSION_KEY = "SiteHelpPanelOpen";
const HELP_ENTRIES_CACHE_KEY = "HelpEntriesBySectionCache";
const DEFAULT_HELP_PANEL_WIDTH = 320;
const DEFAULT_HELP_PANEL_HEIGHT = 180;

function helpEntriesCacheKey(companyId: number, sectionName: string): string {
  return `${companyId}:${sectionName.toLowerCase()}`;
}

function readCachedHelpEntries(companyId: number, sectionName: string): HelpEntry[] | null {
  try {
    const raw = window.sessionStorage.getItem(HELP_ENTRIES_CACHE_KEY);
    if (!raw) return null;
    const cache = JSON.parse(raw) as Record<string, HelpEntry[]>;
    return cache[helpEntriesCacheKey(companyId, sectionName)] ?? null;
  } catch {
    return null;
  }
}

function writeCachedHelpEntries(companyId: number, sectionName: string, entries: HelpEntry[]) {
  try {
    const raw = window.sessionStorage.getItem(HELP_ENTRIES_CACHE_KEY);
    const cache = raw ? (JSON.parse(raw) as Record<string, HelpEntry[]>) : {};
    cache[helpEntriesCacheKey(companyId, sectionName)] = entries;
    window.sessionStorage.setItem(HELP_ENTRIES_CACHE_KEY, JSON.stringify(cache));
  } catch {
  }
}

function clearCachedHelpEntries(companyId: number, sectionName: string) {
  try {
    const raw = window.sessionStorage.getItem(HELP_ENTRIES_CACHE_KEY);
    if (!raw) return;
    const cache = JSON.parse(raw) as Record<string, HelpEntry[]>;
    delete cache[helpEntriesCacheKey(companyId, sectionName)];
    window.sessionStorage.setItem(HELP_ENTRIES_CACHE_KEY, JSON.stringify(cache));
  } catch {
  }
}

function resolveHelpEntry(
  entries: HelpEntry[],
  helpId: string | null,
  sectionName: string,
  companyId: number,
): HelpEntry | null {
  if (!helpId || !Array.isArray(entries) || entries.length === 0) return null;

  const normalizedId = helpId.toLowerCase();
  const cid = companyId;

  const sectionCompanySpecific = cid > 0
    ? entries.find((e) => (e.identifier || "").toLowerCase() === normalizedId && e.companyId === cid && e.section === sectionName)
    : null;
  if (sectionCompanySpecific) return sectionCompanySpecific;

  const sectionGeneric = entries.find(
    (e) => (e.identifier || "").toLowerCase() === normalizedId
      && (e.companyId == null || e.companyId === undefined)
      && e.section === sectionName,
  );
  if (sectionGeneric) return sectionGeneric;

  const globalCompanySpecific = cid > 0
    ? entries.find(
      (e) => (e.identifier || "").toLowerCase() === normalizedId
        && e.companyId === cid
        && !(e.section ?? "").trim(),
    )
    : null;
  if (globalCompanySpecific) return globalCompanySpecific;

  const globalGeneric = entries.find(
    (e) => (e.identifier || "").toLowerCase() === normalizedId
      && (e.companyId == null || e.companyId === undefined)
      && !(e.section ?? "").trim(),
  );
  if (globalGeneric) return globalGeneric;

  return null;
}

export default function AdminTitle(props: any) {
  const { activeTab, backUrl, company, updateSpinner, helpScope } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const [isPopupOpen, updatePopupOpen] = useState(false);
  const [popupTitle] = useState("");
  const [popupContent] = useState("");
  const [websiteVersion, updateWebsiteVersion] = useState("1.1.1");
  const [apiVersion, updateApiVersion] = useState("2.2.2");
  const [showUserEdit, setShowUserEdit] = useState(false);
  const [showReleaseHistory, setShowReleaseHistory] = useState(false);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [showHelpPanel, setShowHelpPanel] = useState(() => {
    try {
      const stored = window.sessionStorage.getItem(SITE_HELP_SESSION_KEY);
      if (stored == null) return false;
      return stored === "true";
    } catch {
      return false;
    }
  });
  const [helpEntries, setHelpEntries] = useState<HelpEntry[]>([]);
  const [supportedLanguages, setSupportedLanguages] = useState<SupportedLanguage[]>([]);
  const [hoveredHelpId, setHoveredHelpId] = useState<string | null>(null);
  const [isAltHeld, setIsAltHeld] = useState(false);
  const [showHelpEdit, setShowHelpEdit] = useState(false);
  const [helpEditEntry, setHelpEditEntry] = useState<HelpEntry | null>(null);
  const [helpEditGenericEntry, setHelpEditGenericEntry] = useState<HelpEntry | null>(null);
  const helpPanelRef = useRef<HTMLElement | null>(null);
  
  // Read environment during render instead of storing in state
  const environment = import.meta.env.VITE_ENV || "PROD";
  const searchTab = useMemo(() => {
    try {
      return new URLSearchParams(location.search).get("tab");
    } catch {
      return null;
    }
  }, [location.search]);
  const resolvedTab = activeTab || searchTab || "default";
  const resolvedHelpScope = helpScope || (location.pathname.toLowerCase().startsWith("/company/") ? "company" : "admin");
  const helpSectionName = `${resolvedHelpScope}.${resolvedTab}`;
  const isSysAdmin = useMemo(() => readPermIdsFromSession().includes(Enums.ROLE_PERMS.SystemAdmin), [currentUser]);

  useEffect(() => {
    updateVersionAsync();
    // Get current user ID from session storage
    const decodedUser = safeReadCurrentUserFromSession();
    if (decodedUser) {
      setCurrentUser(decodedUser);
      if (typeof decodedUser?.showHelp === "boolean") {
        setShowHelpPanel(decodedUser.showHelp);
      }
    }
  }, []);

  function updateSessionUserShowHelp(nextShowHelp: boolean) {
    try {
      const rawUser = window.sessionStorage.getItem("User");
      if (!rawUser) return;

      const decoded = JSON.parse(atob(rawUser));
      const updated = {
        ...decoded,
        showHelp: nextShowHelp,
      };

      window.sessionStorage.setItem("User", btoa(JSON.stringify(updated)));
      setCurrentUser((prev) => ({ ...(prev || {}), showHelp: nextShowHelp }));
    } catch {
    }
  }

  async function fetchHelpEntriesForSectionAsync(forceRefresh = false, includeDetail = false): Promise<HelpEntry[]> {
    const companyId = company?.id ?? 0;
    if (!forceRefresh && !includeDetail) {
      const cached = readCachedHelpEntries(companyId, helpSectionName);
      if (cached) return cached;
    }

    const baseUrl = companyId
      ? `/Help/Company/${companyId}/Section/${encodeURIComponent(helpSectionName)}`
      : `/Help/Section/${encodeURIComponent(helpSectionName)}`;
    const url = includeDetail ? `${baseUrl}?includeDetail=true` : baseUrl;

    const resp = await RightTimeApi.getAsync(url);
    if (resp?.ok && Array.isArray(resp.jsonData)) {
      if (!includeDetail) {
        writeCachedHelpEntries(companyId, helpSectionName, resp.jsonData);
      }
      return resp.jsonData;
    }
    return [];
  }

  async function loadHelpEntriesAsync(forceRefresh = false) {
    if (!showHelpPanel) {
      setHelpEntries([]);
      return;
    }

    const entries = await fetchHelpEntriesForSectionAsync(forceRefresh);
    setHelpEntries(entries);
  }

  function resolveHelpEntryForEdit(entries: HelpEntry[], clickedHelpId: string): {
    helpEntry: HelpEntry;
    genericEntry: HelpEntry | null;
  } {
    const cid = company?.id ?? 0;
    const normalizedId = clickedHelpId.toLowerCase();

    const sectionCompanyEntry = cid > 0
      ? entries.find((e) => (e.identifier || "").toLowerCase() === normalizedId && e.companyId === cid && e.section === helpSectionName)
      : null;
    const sectionGenericEntry = entries.find((e) => (e.identifier || "").toLowerCase() === normalizedId && (e.companyId == null || e.companyId === undefined) && e.section === helpSectionName) ?? null;
    const globalCompanyEntry = cid > 0
      ? entries.find((e) => (e.identifier || "").toLowerCase() === normalizedId && e.companyId === cid && !(e.section ?? "").trim())
      : null;
    const globalGenericEntry = entries.find((e) => (e.identifier || "").toLowerCase() === normalizedId && (e.companyId == null || e.companyId === undefined) && !(e.section ?? "").trim()) ?? null;
    const existingEntry = entries.find((entry) => (entry.identifier || "").toLowerCase() === normalizedId) ?? null;

    return {
      helpEntry:
        sectionCompanyEntry
        ?? sectionGenericEntry
        ?? globalCompanyEntry
        ?? globalGenericEntry
        ?? existingEntry
        ?? { id: 0, section: helpSectionName, identifier: clickedHelpId, languages: [] },
      genericEntry: sectionGenericEntry ?? globalGenericEntry,
    };
  }

  async function ensureSupportedLanguagesAsync() {
    if (supportedLanguages.length > 0) return;
    const resp = await RightTimeApi.getAsync("/Languages");
    if (resp?.ok && Array.isArray(resp.jsonData)) {
      setSupportedLanguages(filterSupportedLanguages(resp.jsonData));
    }
  }

  async function toggleHelpPanelAsync() {
    if (!currentUser?.userId) {
      setShowHelpPanel((prev) => !prev);
      return;
    }

    updateSpinner?.(true);
    try {
      const resp = await RightTimeApi.putAsync(`/User/${currentUser.userId}/ShowHelpToggle`);
      if (resp?.ok) {
        const nextState = !!resp?.jsonData?.showHelp;
        setShowHelpPanel(nextState);
        updateSessionUserShowHelp(nextState);
        return;
      }
    } finally {
      updateSpinner?.(false);
    }

    setShowHelpPanel((prev) => !prev);
  }

  useEffect(() => {
    try {
      window.sessionStorage.setItem(SITE_HELP_SESSION_KEY, String(showHelpPanel));
    } catch {
    }
  }, [showHelpPanel]);

  useEffect(() => {
    loadHelpEntriesAsync();
  }, [showHelpPanel, helpSectionName]);

  useEffect(() => {
    if (!showHelpPanel) {
      setHoveredHelpId(null);
      setIsAltHeld(false);
      return;
    }

    const updatePanelPosition = (event: MouseEvent, target: Element | null) => {
      const panel = helpPanelRef.current;
      if (!panel) return;

      const panelWidth = panel.offsetWidth || DEFAULT_HELP_PANEL_WIDTH;
      const panelHeight = panel.offsetHeight || DEFAULT_HELP_PANEL_HEIGHT;
      const edgePadding = 12;
      const preferredLeft = event.clientX + 100;
      const wouldOverflowRight = preferredLeft + panelWidth > window.innerWidth - edgePadding;
      const rawLeft = wouldOverflowRight
        ? event.clientX - 100 - panelWidth
        : preferredLeft;
      const left = Math.max(edgePadding, Math.min(rawLeft, window.innerWidth - panelWidth - edgePadding));

      const targetRect = target?.getBoundingClientRect();
      const targetBottom = targetRect?.bottom ?? event.clientY;
      const targetTop = targetRect?.top ?? event.clientY;
      const belowTop = targetBottom + 12;
      const aboveTop = targetTop - panelHeight - 12;
      const shouldShowAbove = belowTop + panelHeight > window.innerHeight - edgePadding && aboveTop >= edgePadding;
      const rawTop = shouldShowAbove ? aboveTop : belowTop;
      const top = Math.max(edgePadding, Math.min(rawTop, window.innerHeight - panelHeight - edgePadding));

      panel.style.setProperty("--site-help-left", `${left}px`);
      panel.style.setProperty("--site-help-top", `${top}px`);
    };

    const resolveHelpTarget = (target: EventTarget | null) => {
      let node: Node | null = target instanceof Node ? target : null;

      // Walk up the DOM tree so SVG child nodes (e.g. <path>) still resolve
      // to a parent carrying data-help-id.
      while (node) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          if (element.hasAttribute("data-help-id")) {
            return element;
          }
        }
        node = node.parentNode;
      }

      return null;
    };

    const onMouseMove = (event: MouseEvent) => {
      const target = resolveHelpTarget(event.target);
      const nextHelpId = target?.getAttribute("data-help-id") || null;
      setHoveredHelpId(nextHelpId);
      setIsAltHeld(isSysAdmin && event.altKey);
      if (nextHelpId) {
        updatePanelPosition(event, target);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isSysAdmin) return;
      if (event.key === "Alt") setIsAltHeld(true);
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (!isSysAdmin) return;
      if (event.key === "Alt") setIsAltHeld(false);
    };

    const onMouseLeaveWindow = () => {
      setHoveredHelpId(null);
    };

    const onDocumentClick = async (event: MouseEvent) => {
      const target = resolveHelpTarget(event.target);
      const clickedHelpId = target?.getAttribute("data-help-id") || null;
      if (!clickedHelpId) return;

      if (event.ctrlKey && showHelpPanel) {
        const entries = await fetchHelpEntriesForSectionAsync(false, true);
        const entry = resolveHelpEntry(entries, clickedHelpId, helpSectionName, company?.id ?? 0);
        if (entry && helpEntryHasDetailContent(entry)) {
          const detailPath = buildHelpDetailPagePath(entry.id);
          if (detailPath) {
            event.preventDefault();
            event.stopPropagation();
            if (typeof event.stopImmediatePropagation === "function") {
              event.stopImmediatePropagation();
            }
            window.open(detailPath, "_blank");
          }
        }
        return;
      }

      if (!isSysAdmin) return;
      if (!clickedHelpId) return;

      // Alt+click is the explicit "edit this help item" gesture.
      if (!event.altKey) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }

      const entries = await fetchHelpEntriesForSectionAsync();
      if (showHelpPanel) {
        setHelpEntries(entries);
      }

      const { helpEntry, genericEntry } = resolveHelpEntryForEdit(entries, clickedHelpId);

      await ensureSupportedLanguagesAsync();
      setHelpEditGenericEntry(genericEntry);
      setHelpEditEntry(helpEntry);
      setShowHelpEdit(true);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseleave", onMouseLeaveWindow);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup", onKeyUp);
    document.addEventListener("click", onDocumentClick, true);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseleave", onMouseLeaveWindow);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keyup", onKeyUp);
      document.removeEventListener("click", onDocumentClick, true);
    };
  }, [showHelpPanel, helpEntries, helpSectionName, supportedLanguages.length, isSysAdmin, company?.id]);

  const selectedHelpEntry = useMemo(() => {
    if (!Array.isArray(helpEntries) || helpEntries.length === 0) return null;
    if (hoveredHelpId) {
      return resolveHelpEntry(helpEntries, hoveredHelpId, helpSectionName, company?.id ?? 0);
    }
    return helpEntries.find((entry) => (entry.identifier || "").toLowerCase() === "general") || null;
  }, [helpEntries, hoveredHelpId, helpSectionName, company?.id]);

  const selectedHelpLanguage = useMemo(() => {
    if (!selectedHelpEntry?.languages || selectedHelpEntry.languages.length === 0) return null;
    const preferredLanguageId = String(currentUser?.languageId ?? currentUser?.defaultLanguageId ?? 1);
    const normalizedPreferred = preferredLanguageId.toLowerCase();

    const exactLanguage = selectedHelpEntry.languages.find((lang) => String(lang.languageId || "").toLowerCase() === normalizedPreferred);
    if (exactLanguage) return decodeHelpLanguage(exactLanguage);

    const englishLanguage = selectedHelpEntry.languages.find((lang) => {
      const key = String(lang.languageId || "").toLowerCase();
      return key === "en" || key === "eng" || key === "1";
    });
    if (englishLanguage) return decodeHelpLanguage(englishLanguage);

    return decodeHelpLanguage(selectedHelpEntry.languages[0]);
  }, [selectedHelpEntry, currentUser?.languageId, currentUser?.defaultLanguageId]);

  const selectedHelpFormat = useMemo(() => {
    return readHelpEntryFormat(selectedHelpEntry);
  }, [selectedHelpEntry]);

  const hasSpecificHelpEntry = useMemo(() => {
    if (!hoveredHelpId) return false;
    return helpEntries.some((entry) => (entry.identifier || "").toLowerCase() === hoveredHelpId.toLowerCase());
  }, [helpEntries, hoveredHelpId]);

  const selectedHelpBody = selectedHelpLanguage?.content || (hasSpecificHelpEntry ? "" : "No help content exists for this element yet. Click this element to create help content.");

  const selectedHelpTitle = (selectedHelpLanguage?.title ?? "").trim();

  const helpPanelTitle = useMemo(() => {
    if (selectedHelpFormat === Enums.CONTENT_FORMAT.HTML && !selectedHelpTitle) {
      return null;
    }
    if (selectedHelpTitle) {
      return selectedHelpTitle;
    }
    return hasSpecificHelpEntry ? "Help" : "Help Not Defined";
  }, [selectedHelpFormat, selectedHelpTitle, hasSpecificHelpEntry]);

  const selectedHelpHasDetailContent = useMemo(
    () => helpEntryHasDetailContent(selectedHelpEntry),
    [selectedHelpEntry],
  );

  async function updateVersionAsync() {
    const webVersion = Helpers.AppVersion();
    updateWebsiteVersion(webVersion);

    const apiVersion = await RightTimeApi.versionAsync();
    if (apiVersion) {
      updateApiVersion(apiVersion);
    }

    let userId: number | null = null;
    const userData = window.sessionStorage.getItem("User");
    if (userData) {
      try {
        userId = JSON.parse(atob(userData))?.userId ?? null;
      } catch {
        userId = null;
      }
    }

    await auditVersionChangesAsync({
      webVersion,
      apiVersion: apiVersion ?? null,
      companyId: company?.id ?? null,
      userId,
    });
  }

  function logout() {
    clearSiteData();
    navigate("/");
  }

  function onBack() {
    if (backUrl && backUrl !== "-1") {
      let url = backUrl;
      navigate(url);
    } else navigate(-1);
  }


  var companyImageUrl = "";
  if (props && company && company.id && company.hasImage) {
    companyImageUrl = RightTimeApi.URL("/Image/" + company.imageId);
  }
  return (
    <>
      <div className={`pageTitle ${environment !== "PROD" ? "nonProd" : ""}`}>
        <span className="titleLeft">
          {backUrl && backUrl.length > 0 ? (
            <IoArrowBack className="actionIcon react-icon" onClick={onBack} />
          ) : null}
        </span>

        <span className={`companyDesignation${resolvedTab === "kpsi" ? " kpsi" : ""}`}>
          {resolvedTab === "kpsi" ? (
            <img
              className="titleCompanyLogo kpsiLogo"
              src={RightTimeApi.URL("/Kpsi/Logo")}
              alt="KPSI"
            />
          ) : (
            <>
              {/* {environment === "PROD" && companyImageUrl && <img className="titleCompanyLogo" src={companyImageUrl} alt="" />} */}
              <span>{props?.company?.name}</span>
            </>
          )}
        </span>
        <span className="titleRight">
          <button
            type="button"
            className={`recruitHelpToggle${showHelpPanel ? " active" : ""}`}
            onClick={toggleHelpPanelAsync}
            title={showHelpPanel ? "Hide help" : "Show help"}
          >
            <span className="recruitHelpToggleLabel">?</span>
          </button>
          <BsPersonCircle
            title={currentUser ? (currentUser.firstName + " " + currentUser.lastName) : "My Account"}
            className="actionIcon react-icon userIcon"
            onClick={() => {
              if (currentUser?.userId) {
                setShowUserEdit(true);
              }
            }}
          />
          <TbLogout title="Log Out" className="actionIcon react-icon" onClick={logout} />
        </span>
        <span
          className={`version${isSysAdmin ? " clickable" : ""}`}
          onClick={isSysAdmin ? () => setShowReleaseHistory(true) : undefined}
          title={isSysAdmin ? "Release history" : undefined}
          role={isSysAdmin ? "button" : undefined}
        >
          {environment !== "PROD" && <span>{environment} </span>}
          {websiteVersion} - {apiVersion}
        </span>
      </div>
      {isPopupOpen ? (
        <div className="popup">
          <div className="popupTitle">{popupTitle || "Account"}</div>
          <div className="popupContent">{popupContent || "My Account Settings"}</div>
        </div>
      ) : null}

      <aside
        ref={helpPanelRef}
        className={`siteHelpPanel${showHelpPanel && hoveredHelpId ? " isOpen" : ""}`}
      >
        {isSysAdmin && hoveredHelpId ? (
          <div className="siteHelpPanelAdminHint">&lt;ALT&gt; to modify</div>
        ) : null}
        {selectedHelpHasDetailContent && hoveredHelpId ? (
          <div className="siteHelpPanelDetailHint">&lt;CTRL&gt;+click this element for detailed instructions</div>
        ) : null}
        {helpPanelTitle ? (
          <div className="siteHelpPanelTitle">{helpPanelTitle}</div>
        ) : null}
        {isSysAdmin && isAltHeld && hoveredHelpId ? (
          <div className="siteHelpPanelHelpId">{hoveredHelpId}</div>
        ) : null}
        <div className="siteHelpPanelBody">
          {selectedHelpFormat === Enums.CONTENT_FORMAT.HTML ? (
            <div className="siteHelpPanelRenderedHtml">
              {renderFormattedContent(selectedHelpBody, { format: Enums.CONTENT_FORMAT.HTML })}
            </div>
          ) : (
            selectedHelpBody
              .split(/\r?\n/)
              .filter(Boolean)
              .map((line, index) => (
                <p key={`${selectedHelpEntry?.id || "help"}-${index}`}>{line}</p>
              ))
          )}
        </div>
      </aside>

      <HelpEditModal
        isOpen={showHelpEdit}
        onClose={() => {
          setShowHelpEdit(false);
          setHelpEditEntry(null);
        }}
        helpEntry={helpEditEntry}
        genericEntry={helpEditGenericEntry}
        companyId={company?.id ?? null}
        supportedLanguages={supportedLanguages}
        initialLanguageId={1}
        onSaved={() => {
          clearCachedHelpEntries(company?.id ?? 0, helpSectionName);
          loadHelpEntriesAsync(true);
        }}
      />
      
      {/* User Account Modal */}
      {showUserEdit && currentUser?.userId && (
        <UserEditModal
          isOpen={showUserEdit}
          onClose={() => setShowUserEdit(false)}
          userId={currentUser.userId}
          companyData={company}
          updateSpinner={updateSpinner || null}
          adminPerms={company?.permissions || []}
        />
      )}

      <ReleaseHistoryModal
        isOpen={showReleaseHistory}
        onClose={() => setShowReleaseHistory(false)}
        updateSpinner={updateSpinner || null}
      />
    </>
  );
}

